// utils/browserEscape.js
//
// The URLs that get a page out of another app's browser view and into a real
// browser (pack prompt 7 Parts B and C, owner research 2026-11-03). Pure: no
// adapter, no store, no service, so the login and welcome screens can import
// it without dragging the platform adapter into their tests.
//
//  - Android: Chrome's `intent://` form. Chrome opens the link when present;
//    `S.browser_fallback_url` (the same https link) opens in the current view
//    when it is not. Honoured by Chrome's rules from a real tap on a link;
//    Facebook and Messenger follow them, Instagram reportedly ignores them, and
//    Telegram refuses intents from a Mini App altogether (TmaAdapter uses
//    Telegram's own browser preference instead — never this).
//  - iPhone: the undocumented `x-safari-https://` scheme, which Telegram's own
//    iOS client uses to force Safari on iOS 18+. Blocked by Instagram, flaky
//    across iOS updates — so every caller keeps the "⋯ then open in browser"
//    hint and a copy-link beside it. Failures are silent.

/**
 * The page the member is on, with the hash dropped and the OIDC error
 * parameters (`error`, `error_description`) removed: they describe a sign-in
 * that already failed here, and must not greet him again in the real browser.
 *
 * @returns {string} absolute URL, '' outside a window
 */
export function currentPageUrl() {
  if (typeof window === 'undefined' || !window.location) return '';
  const { origin, pathname, search } = window.location;
  const params = new URLSearchParams(search);
  params.delete('error');
  params.delete('error_description');
  const query = params.toString();
  return `${origin}${pathname}${query ? `?${query}` : ''}`;
}

/**
 * Chrome's Android intent URL for an https link, with the link itself as the
 * fallback. Reference: https://developer.chrome.com/docs/android/intents
 *
 * @param {string} url absolute https URL
 * @returns {string}
 */
export function chromeIntentUrlFor(url) {
  // A fragment would end the data part early and hide the #Intent block from
  // Android's parser (nothing opens, no fallback); the fallback keeps it.
  const [hostAndPath] = url.replace(/^https?:\/\//, '').split('#');
  return (
    `intent://${hostAndPath}` +
    '#Intent;scheme=https;package=com.android.chrome;' +
    `S.browser_fallback_url=${encodeURIComponent(url)};end`
  );
}

/**
 * The Safari-forcing form of an https link on iPhone.
 *
 * @param {string} url absolute https URL
 * @returns {string}
 */
export function safariUrlFor(url) {
  return url.replace(/^https?:\/\//, 'x-safari-https://');
}
